import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config/api';
import { 
  BookOpen, 
  Award, 
  CheckCircle,
  Trophy,
  Brain,
  Building2,
  Zap,
  ExternalLink,
  RefreshCcw,
  Play,
  ArrowRight,
  TrendingUp,
  Clock,
  AlertCircle,
  Globe
} from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import CompetencyFrameworkTab from '../components/CompetencyFrameworkTab';
// Removed: GlobalRoleReadinessAssessment - replaced by CompetencyFrameworkTab
import { CourseRecommendations } from '../components/CourseRecommendations';
import GamificationDashboard from '../components/GamificationDashboard';
import LearnerInsights from '../components/LearnerInsights';
import { Progress } from '../components/ui/progress';
const API = `${API_BASE}/api`;

export default function DashboardPage() {
  const { user, token } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [enrollments, setEnrollments] = useState([]);
  const [externalEnrollments, setExternalEnrollments] = useState([]);
  const [orgRecommendations, setOrgRecommendations] = useState({ courses: [], organization: null });
  const [activeTab, setActiveTab] = useState('overview');
  const [gamificationProfile, setGamificationProfile] = useState(null);

  // Feature flags
  const { isFeatureEnabled } = useFeatureFlags();
  // Message carried over a redirect (e.g. course moved back to draft)
  useEffect(() => {
    const msg = sessionStorage.getItem('cerebro_flash_warning');
    if (msg) {
      sessionStorage.removeItem('cerebro_flash_warning');
      import('sonner').then(({ toast }) => toast.warning(msg, { duration: 8000 }));
    }
  }, []);
  const showRoleProfiling = isFeatureEnabled('role_profiling');

  // Categorize enrollments by status
  // In Progress: active, in_progress
  // Completed: completed (passed)
  // Failed: failed (needs retake)
  const inProgressCourses = enrollments.filter(e => e.status === 'active' || e.status === 'in_progress');
  const completedCourses = enrollments.filter(e => e.status === 'completed');
  const failedCourses = enrollments.filter(e => e.status === 'failed');
  
  // External course stats
  const externalInProgress = externalEnrollments.filter(e => e.status === 'active');
  const externalCompleted = externalEnrollments.filter(e => e.status === 'completed');

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!token) return;

      try {
        const headers = { Authorization: `Bearer ${token}` };
        
        // Fetch only essential data for faster load
        // Enrollments now include course details (title, slug) from backend
        const [enrollRes, orgRecsRes, gamRes, externalRes] = await Promise.all([
          axios.get(`${API}/enrollments`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/organizations/my/recommended-courses`, { headers }).catch(() => ({ data: { courses: [], organization: null } })),
          axios.get(`${API}/gamification/profile`, { headers }).catch(() => ({ data: null })),
          axios.get(`${API}/external-courses/my-enrollments`, { headers }).catch(() => ({ data: [] }))
        ]);

        setEnrollments(enrollRes.data || []);
        setOrgRecommendations(orgRecsRes.data || { courses: [], organization: null });
        setGamificationProfile(gamRes.data);
        setExternalEnrollments(externalRes.data || []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [token]);

  // Handle course retake
  const handleRetake = async (enrollmentId) => {
    try {
      await axios.post(`${API}/enrollments/${enrollmentId}/retake`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Refresh enrollments
      const res = await axios.get(`${API}/enrollments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEnrollments(res.data || []);
    } catch (error) {
      console.error('Error retaking course:', error);
    }
  };

  // Quick stats
  const stats = [
    { 
      icon: BookOpen, 
      value: inProgressCourses.length, 
      label: 'In Progress',
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10'
    },
    { 
      icon: CheckCircle, 
      value: completedCourses.length, 
      label: 'Completed',
      color: 'text-green-400',
      bgColor: 'bg-green-500/10'
    },
    { 
      icon: Zap, 
      value: gamificationProfile?.total_xp || 0, 
      label: 'Total XP',
      color: 'text-navigate-gold',
      bgColor: 'bg-navigate-gold/10'
    },
    { 
      icon: Trophy, 
      value: `Level ${gamificationProfile?.level || 1}`, 
      label: gamificationProfile?.level_name || 'Novice',
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10'
    },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-zinc-800 rounded w-64" />
            <div className="grid grid-cols-4 gap-4">
              {[1,2,3,4].map(i => <div key={i} className="h-24 bg-zinc-800 rounded-xl" />)}
            </div>
            <div className="h-64 bg-zinc-800 rounded-xl" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section with Quick Stats */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h1 className="font-heading text-3xl font-bold text-white mb-1">
                Welcome back, {user?.full_name?.split(' ')[0] || 'Learner'}
              </h1>
              <p className="text-zinc-400 text-sm">
                Continue your learning journey
              </p>
            </div>
          </div>

          {/* Quick Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {stats.map((stat, index) => (
              <div 
                key={index}
                className={`${stat.bgColor} border border-zinc-800/50 rounded-xl p-4 flex items-center gap-3`}
              >
                <stat.icon className={`h-8 w-8 ${stat.color}`} />
                <div>
                  <div className="font-heading text-xl font-bold text-white">
                    {stat.value}
                  </div>
                  <div className="text-xs text-zinc-400">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tab Navigation */}
        {showRoleProfiling && (
          <div className="flex flex-wrap gap-2 mb-6 border-b border-zinc-800 pb-4">
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                activeTab === 'overview'
                  ? 'bg-navigate-gold text-black font-medium'
                  : 'bg-zinc-800/50 text-zinc-400 hover:text-white hover:bg-zinc-800'
              }`}
            >
              <BookOpen className="h-4 w-4" />
              Learning Overview
            </button>
            <button
              onClick={() => setActiveTab('insights')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                activeTab === 'insights'
                  ? 'bg-navigate-gold text-black font-medium'
                  : 'bg-zinc-800/50 text-zinc-400 hover:text-white hover:bg-zinc-800'
              }`}
            >
              <TrendingUp className="h-4 w-4" />
              Progress Insights
            </button>
            <button
              onClick={() => setActiveTab('achievements')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                activeTab === 'achievements'
                  ? 'bg-navigate-gold text-black font-medium'
                  : 'bg-zinc-800/50 text-zinc-400 hover:text-white hover:bg-zinc-800'
              }`}
              data-testid="achievements-tab"
            >
              <Trophy className="h-4 w-4" />
              Achievements
            </button>
            <button
              onClick={() => setActiveTab('role-profile')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                activeTab === 'role-profile'
                  ? 'bg-navigate-gold text-black font-medium'
                  : 'bg-zinc-800/50 text-zinc-400 hover:text-white hover:bg-zinc-800'
              }`}
              data-testid="role-profile-tab"
            >
              <Brain className="h-4 w-4" />
              Competency Framework
            </button>
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'role-profile' ? (
          <CompetencyFrameworkTab />
        ) : activeTab === 'achievements' ? (
          <GamificationDashboard />
        ) : activeTab === 'insights' ? (
          <LearnerInsights token={token} />
        ) : (
          <div className="space-y-8">
            {/* Organization Recommended Courses */}
            {orgRecommendations.organization && orgRecommendations.courses.length > 0 && (
              <section className="bg-gradient-to-r from-blue-900/20 to-transparent border border-blue-500/20 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-9 w-9 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-blue-400" />
                  </div>
                  <div>
                    <h2 className="font-heading text-lg font-semibold text-white">
                      Recommended by {orgRecommendations.organization.name}
                    </h2>
                    <p className="text-xs text-zinc-400">
                      Assigned courses for your professional development
                    </p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {orgRecommendations.courses.slice(0, 3).map((course) => (
                    <Link
                      key={course.course_id}
                      to={`/courses/${course.slug || course.course_id}`}
                      className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3 hover:border-blue-500/50 transition-colors group"
                    >
                      <h4 className="font-medium text-white text-sm group-hover:text-blue-400 line-clamp-1">
                        {course.title}
                      </h4>
                      <p className="text-xs text-zinc-500 mt-1 line-clamp-1">{course.category}</p>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* My Courses Table */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl font-semibold text-white">
                  My Courses
                </h2>
                <Link 
                  to="/courses"
                  className="text-sm text-navigate-gold hover:text-navigate-gold-light flex items-center gap-1"
                >
                  Browse All <ArrowRight className="h-3 w-3" />
                </Link>
              </div>

              {enrollments.length === 0 ? (
                <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-8 text-center">
                  <BookOpen className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">
                    No courses enrolled
                  </h3>
                  <p className="text-zinc-400 mb-4 text-sm">
                    Start your learning journey by enrolling in a course.
                  </p>
                  <Link to="/courses" className="inline-flex items-center gap-2 bg-navigate-gold text-black px-4 py-2 rounded-lg font-medium hover:bg-navigate-gold/90 transition-colors">
                    Browse Courses
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              ) : (
                <div className="bg-navigate-surface border border-zinc-800 rounded-xl overflow-hidden">
                  {/* Table Header */}
                  <div className="hidden md:grid md:grid-cols-12 gap-4 px-4 py-3 bg-zinc-900/50 border-b border-zinc-800 text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    <div className="col-span-5">Course Name</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-2">Progress</div>
                    <div className="col-span-2">XP Earned</div>
                    <div className="col-span-1 text-right">Action</div>
                  </div>

                  {/* Table Rows */}
                  <div className="divide-y divide-zinc-800">
                    {enrollments.map((enrollment) => {
                      const isCompleted = enrollment.status === 'completed';
                      const isFailed = enrollment.status === 'failed';
                      const isInProgress = enrollment.status === 'in_progress' || (enrollment.status === 'active' && enrollment.progress_percent > 0);
                      const isNotStarted = enrollment.status === 'active' && (!enrollment.progress_percent || enrollment.progress_percent === 0);
                      const courseUrl = enrollment.course_slug || enrollment.course_id;
                      const isAiCourse = enrollment.course_id?.startsWith('ai_course_');
                      
                      // Show retake for both completed and failed courses
                      const showRetake = isCompleted || isFailed;
                      // Course moved back to draft: progress kept, access blocked
                      const isUnavailable = enrollment.course_available === false;
                      
                      return (
                        <div 
                          key={enrollment.enrollment_id}
                          className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 p-4 hover:bg-zinc-800/30 transition-colors items-center"
                        >
                          {/* Course Name */}
                          <div className="md:col-span-5">
                            {isUnavailable ? (
                              <>
                                <span className="font-medium text-zinc-400 line-clamp-1">{enrollment.course_title || 'Untitled Course'}</span>
                                <p className="text-xs text-amber-400 mt-0.5" data-testid={`enrollment-unavailable-${enrollment.course_id}`}>
                                  {enrollment.course_unavailable_message || 'This course is temporarily unavailable. Your progress has been saved.'}
                                </p>
                              </>
                            ) : (
                            <Link 
                              to={`/courses/${courseUrl}`}
                              className="font-medium text-white hover:text-navigate-gold transition-colors line-clamp-1 flex items-center gap-2"
                            >
                              {enrollment.course_title || 'Untitled Course'}
                              <ExternalLink className="h-3 w-3 text-zinc-500 shrink-0" />
                            </Link>
                            )}
                            <p className="text-xs text-zinc-500 mt-0.5 md:hidden">
                              {isCompleted ? 'Completed' : isFailed ? 'Failed' : isInProgress ? 'In Progress' : 'Not Started'}
                            </p>
                          </div>

                          {/* Status */}
                          <div className="md:col-span-2 hidden md:block">
                            {isCompleted ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-green-400 bg-green-500/10 px-2 py-1 rounded">
                                <CheckCircle className="h-3 w-3" />
                                Completed
                              </span>
                            ) : isFailed ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-red-400 bg-red-500/10 px-2 py-1 rounded">
                                <AlertCircle className="h-3 w-3" />
                                Failed
                              </span>
                            ) : isInProgress ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-400 bg-blue-500/10 px-2 py-1 rounded">
                                <Clock className="h-3 w-3" />
                                In Progress
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-zinc-400 bg-zinc-500/10 px-2 py-1 rounded">
                                Not Started
                              </span>
                            )}
                          </div>

                          {/* Progress */}
                          <div className="md:col-span-2">
                            <div className="flex items-center gap-2">
                              <Progress value={enrollment.progress_percent || 0} className="h-1.5 flex-1" />
                              <span className="text-xs text-zinc-400 w-8">{enrollment.progress_percent || 0}%</span>
                            </div>
                          </div>

                          {/* XP Earned - Only show for completed (passed) courses */}
                          <div className="md:col-span-2">
                            {isCompleted && enrollment.xp_earned > 0 ? (
                              <span className="inline-flex items-center gap-1 text-sm font-medium text-navigate-gold">
                                <Zap className="h-4 w-4" />
                                +{enrollment.xp_earned} XP
                              </span>
                            ) : isFailed ? (
                              <span className="text-xs text-red-400">No XP (Failed)</span>
                            ) : (
                              <span className="text-xs text-zinc-500">-</span>
                            )}
                          </div>

                          {/* Action - Retake for completed/failed, Continue for in progress */}
                          <div className="md:col-span-1 flex justify-end">
                            {isUnavailable ? (
                              <span className="text-xs text-zinc-500" title="Temporarily unavailable">Unavailable</span>
                            ) : showRetake ? (
                              <button
                                onClick={() => handleRetake(enrollment.enrollment_id)}
                                className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${
                                  isFailed 
                                    ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                                    : 'bg-zinc-500/20 text-zinc-400 hover:bg-zinc-500/30'
                                }`}
                                title="Retake Course"
                              >
                                <RefreshCcw className="h-3 w-3" />
                                Retake
                              </button>
                            ) : (
                              <Link
                                to={`/courses/${courseUrl}${isAiCourse ? '/ai-learn' : ''}`}
                                className="inline-flex items-center gap-1 text-xs bg-navigate-gold/20 text-navigate-gold hover:bg-navigate-gold/30 px-2 py-1 rounded transition-colors"
                              >
                                <Play className="h-3 w-3" />
                                {isInProgress ? 'Continue' : 'Start'}
                              </Link>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </section>

            {/* External Courses Section */}
            {isFeatureEnabled('external_courses') && externalEnrollments.length > 0 && (
              <section>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-purple-500/20 rounded-lg">
                      <Globe className="h-4 w-4 text-purple-400" />
                    </div>
                    <h2 className="font-heading text-xl font-semibold text-white">
                      External Courses
                    </h2>
                    <span className="text-xs text-zinc-500 ml-2">
                      {externalCompleted.length} of {externalEnrollments.length} completed
                    </span>
                  </div>
                  <Link 
                    to="/courses?tab=external"
                    className="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1"
                  >
                    Browse More <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>

                <div className="bg-navigate-surface border border-purple-500/20 rounded-xl overflow-hidden">
                  {/* Table Header */}
                  <div className="hidden md:grid md:grid-cols-12 gap-4 px-4 py-3 bg-purple-500/5 border-b border-purple-500/20 text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    <div className="col-span-5">Course Name</div>
                    <div className="col-span-2">Specialisation</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-2">Duration</div>
                    <div className="col-span-1 text-right">Action</div>
                  </div>

                  {/* Table Rows */}
                  <div className="divide-y divide-purple-500/10">
                    {externalEnrollments.map((enrollment) => {
                      const course = enrollment.course || {};
                      const isCompleted = enrollment.status === 'completed';
                      
                      return (
                        <div 
                          key={enrollment.enrollment_id || enrollment.course_id}
                          className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-4 py-4 hover:bg-purple-500/5 transition-colors"
                        >
                          {/* Course Name */}
                          <div className="md:col-span-5 flex items-center gap-3">
                            <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${
                              isCompleted ? 'bg-green-500/20' : 'bg-purple-500/20'
                            }`}>
                              {isCompleted ? (
                                <CheckCircle className="h-4 w-4 text-green-400" />
                              ) : (
                                <Globe className="h-4 w-4 text-purple-400" />
                              )}
                            </div>
                            <div className="min-w-0">
                              <h4 className="font-medium text-white text-sm truncate">
                                {course.title || enrollment.course_id}
                              </h4>
                              <p className="text-xs text-zinc-500 truncate">
                                {course.provider || 'External Provider'}
                              </p>
                            </div>
                          </div>

                          {/* Specialisation */}
                          <div className="md:col-span-2 flex items-center">
                            <span className="text-xs text-purple-400">
                              {course.specialisation || 'External'}
                            </span>
                          </div>

                          {/* Status */}
                          <div className="md:col-span-2 flex items-center">
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                              isCompleted 
                                ? 'bg-green-500/20 text-green-400'
                                : 'bg-purple-500/20 text-purple-400'
                            }`}>
                              {isCompleted ? (
                                <>
                                  <CheckCircle className="h-3 w-3" />
                                  Completed
                                </>
                              ) : (
                                <>
                                  <Clock className="h-3 w-3" />
                                  In Progress
                                </>
                              )}
                            </span>
                          </div>

                          {/* Duration */}
                          <div className="md:col-span-2 flex items-center text-sm text-zinc-400">
                            {course.estimated_duration || 'Varies'}
                          </div>

                          {/* Action */}
                          <div className="md:col-span-1 flex justify-end">
                            <Link
                              to={`/external-courses/${enrollment.course_id}`}
                              className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${
                                isCompleted
                                  ? 'bg-zinc-500/20 text-zinc-400 hover:bg-zinc-500/30'
                                  : 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
                              }`}
                            >
                              <Play className="h-3 w-3" />
                              {isCompleted ? 'Review' : 'Continue'}
                            </Link>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>
            )}

            {/* Role-Based Course Recommendations */}
            {isFeatureEnabled('courses') && (
            <section>
              <CourseRecommendations 
                token={token} 
                onNavigateToProfile={() => setActiveTab('role-profile')}
              />
            </section>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
